import {
  AssociationResult,
  Badge,
  ConfirmationDialog,
  FailureNotice,
  InlineConfirmation,
  Icon,
  IconButton,
  RichText,
  cn,
  useTranslate,
  CARD_SURFACE,
} from '@athena/ui'
import { useMutation } from '@apollo/client/react'
import { useState } from 'react'

import { DeleteQuestionDocument, PracticeDocument } from '../../graphql/generated/operations.js'
import { useUserErrorText } from '../../lib/user-errors.js'
import type { AuthoredQuestionShape } from './authoredQuestion.js'
import { QuestionEditor } from './QuestionEditor.js'

export interface QuestionListProps {
  questions: readonly AuthoredQuestionShape[]
  editable?: boolean
  practiceId?: string
}

/**
 * The authored view: the questions with their answer key.
 *
 * Reachable only through a practice this tutor owns, and typed as
 * `AuthoredQuestion`, which is a different GraphQL type from the one an
 * attempt returns (V1.md section 5). The correct option carries a tick and
 * the word "Correct", because colour never carries meaning alone.
 */
export function QuestionList({ questions, editable = false, practiceId }: QuestionListProps) {
  const t = useTranslate()
  const userErrorText = useUserErrorText()
  const [editingId, setEditingId] = useState<string>()
  const [deleting, setDeleting] = useState<AuthoredQuestionShape>()
  const [confirmation, setConfirmation] = useState<string>()
  const [deleteError, setDeleteError] = useState<string>()
  const [deleteFailure, setDeleteFailure] = useState<unknown>()
  const [deleteQuestion] = useMutation(DeleteQuestionDocument, {
    refetchQueries: practiceId ? [{ query: PracticeDocument, variables: { id: practiceId } }] : [],
    awaitRefetchQueries: true,
  })
  return (
    <>
      {confirmation ? <InlineConfirmation>{confirmation}</InlineConfirmation> : null}
      <ol className="flex flex-col gap-4" data-presentation="cards">
        {questions.map((question) => (
          <li key={question.id} className={cn('flex flex-col gap-3', CARD_SURFACE, 'p-6')}>
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:justify-between sm:gap-4">
              <h3 className="min-w-0 text-h4 font-semibold text-text text-pretty">
                {question.position + 1}. <RichText inline>{question.prompt}</RichText>
              </h3>
              <Badge status="draft" className="shrink-0 whitespace-nowrap">
                {t(`questionType.${question.type}`)}
              </Badge>
            </div>

            {editable ? (
              <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
                <IconButton
                  icon={<Icon.Pencil />}
                  label={t('practice.questionEdit.edit', [question.position + 1])}
                  onClick={() => setEditingId(question.id)}
                />
                <IconButton
                  icon={<Icon.Trash2 />}
                  label={t('practice.questionEdit.delete', [question.position + 1])}
                  onClick={() => {
                    setDeleteError(undefined)
                    setDeleteFailure(undefined)
                    setDeleting(question)
                  }}
                />
              </div>
            ) : null}

            {question.type === 'MATCHING' ? (
              /* The pairs as the tutor wrote them. Not the review component:
               nothing here is right or wrong, it is what the question says. */
              <ul className={cn('flex flex-col gap-2')}>
                {question.pairs.map((pair) => (
                  <li
                    key={pair.id}
                    className={cn(
                      'flex flex-col gap-1 px-4 py-2 text-body sm:flex-row sm:items-baseline sm:gap-3',
                      'rounded-sm border border-border bg-surface',
                    )}
                  >
                    <span className="font-semibold text-text sm:w-1/3">
                      <RichText inline>{pair.leftText}</RichText>
                    </span>
                    <span aria-hidden="true" className="hidden text-text-muted sm:inline">
                      →
                    </span>
                    <span className="flex-1 text-text-secondary text-pretty">
                      <RichText inline>{pair.rightText}</RichText>
                    </span>
                  </li>
                ))}
              </ul>
            ) : question.type === 'ASSOCIATION' ? (
              // The authored order, written out. `options` arrives in
              // presentation order — what the student is shown — so the list
              // below is the answer over it rather than a repeat of it.
              <AssociationResult
                correctOrder={orderedTexts(question)}
                correctLabel={t('practice.questionList.correctOrder')}
              />
            ) : question.options.length > 0 ? (
              <ul className="flex flex-col gap-2">
                {question.options.map((option) => (
                  <li
                    key={option.id}
                    className={cn(
                      'grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-2 px-4 py-2 text-body sm:flex sm:items-center',
                      option.isCorrect
                        ? 'rounded-sm border border-accent-border bg-accent-light text-text'
                        : 'rounded-sm border border-border bg-surface text-text-secondary',
                    )}
                  >
                    <span className="font-semibold">{option.label}.</span>
                    <span className="min-w-0">
                      <RichText inline>{option.text}</RichText>
                    </span>
                    {option.isCorrect ? (
                      <span className="col-start-2 flex items-center gap-1.5 text-body-sm font-semibold text-accent-text sm:ml-auto sm:shrink-0">
                        <Icon.Check aria-hidden="true" className="size-icon-sm" />
                        {t('practice.questionList.correct')}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-body text-text">
                <span className="font-semibold text-accent-text">
                  {t('practice.questionList.accepted')}
                </span>
                <RichText inline>{question.acceptedAnswers.join(' · ')}</RichText>
              </p>
            )}

            {question.explanation ? (
              <p className="text-body-sm text-text-secondary text-pretty">
                <RichText inline>{question.explanation}</RichText>
              </p>
            ) : null}

            {editingId === question.id && practiceId ? (
              <div className="border-t border-border pt-5">
                <QuestionEditor
                  question={question}
                  practiceId={practiceId}
                  onCancel={() => setEditingId(undefined)}
                  onSaved={() => {
                    setEditingId(undefined)
                    setConfirmation(t('practice.questionEdit.saved'))
                  }}
                />
              </div>
            ) : null}
          </li>
        ))}
      </ol>
      {deleting && practiceId ? (
        <ConfirmationDialog
          open
          onOpenChange={(open) => {
            if (!open) setDeleting(undefined)
          }}
          intent="caution"
          title={t('practice.questionEdit.deleteTitle', [deleting.position + 1])}
          description={t('practice.questionEdit.deleteDescription')}
          confirmLabel={t('practice.questionEdit.deleteConfirm')}
          confirmingLabel={t('common.saving')}
          cancelLabel={t('practice.questionEdit.keep')}
          failure={
            deleteError ? (
              <p className="text-body-sm text-danger">{deleteError}</p>
            ) : deleteFailure === undefined ? undefined : (
              <FailureNotice
                title={t('practice.questionEdit.deleteFailed')}
                error={deleteFailure}
              />
            )
          }
          onConfirm={async () => {
            setDeleteError(undefined)
            setDeleteFailure(undefined)
            try {
              const payload = (await deleteQuestion({ variables: { questionId: deleting.id } }))
                .data?.deleteQuestion
              if (payload?.practice) {
                setConfirmation(t('practice.questionEdit.deleted'))
                return true
              }
              setDeleteError(
                payload?.userErrors[0]
                  ? userErrorText(payload.userErrors[0])
                  : t('practice.questionEdit.deleteFailed'),
              )
              return false
            } catch (error) {
              setDeleteFailure(error)
              return false
            }
          }}
        />
      ) : null}
    </>
  )
}

/** The authored order as text, resolved through the question's own options. */
function orderedTexts(question: AuthoredQuestionShape): string[] {
  const byId = new Map(question.options.map((option) => [option.id, option.text]))
  return question.correctOrder.flatMap((optionId) => {
    const text = byId.get(optionId)
    return text === undefined ? [] : [text]
  })
}

// The type names come from `questionType.<TYPE>` in the dictionary, keyed by
// the GraphQL enum value. The enum is an identifier and stays untranslated;
// its label is product copy and does not.
